import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import {
  JumegaReportService,
  type JumegaRecord,
  type JumegaReportRequest,
} from './jumega-report.service';

interface JumegaParseResult {
  records: JumegaRecord[];
  errorMessage?: string;
}

@Component({
  standalone: false,
  selector: 'invd-jumega-report',
  templateUrl: './jumega-report.component.html',
  styleUrls: ['./jumega-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JumegaReportComponent {
  protected selectedFileName = 'No file chosen';
  protected showValidationAlert = false;
  protected isSuccessAlert = false;
  protected isSubmitting = false;
  protected validationAlertMessage = 'Please input the required information.';
  protected hasFileValidationError = false;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly service = inject(JumegaReportService);
  private selectedFile: File | null = null;
  private readonly acceptedFileExtensions = new Set(['txt', 'csv', 'xls', 'xlsx']);

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.selectedFileName = this.selectedFile?.name ?? 'No file chosen';
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.updateValidationFlags();
    this.cdr.markForCheck();
  }

  protected onReset(fileInput?: HTMLInputElement): void {
    this.selectedFile = null;
    this.selectedFileName = 'No file chosen';
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.isSubmitting = false;
    this.hasFileValidationError = false;
    if (fileInput) {
      fileInput.value = '';
    }
    this.cdr.markForCheck();
  }

  protected async onEmailReport(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.updateValidationFlags();

    if (!this.selectedFile) {
      this.validationAlertMessage = 'Please input the required information.';
      this.showValidationAlert = true;
      this.cdr.markForCheck();
      return;
    }

    if (!this.isValidFile()) {
      this.validationAlertMessage = 'Formats accepted: txt, csv, xls, xlsx.';
      this.showValidationAlert = true;
      this.cdr.markForCheck();
      return;
    }

    let parseResult: JumegaParseResult;
    try {
      parseResult = await this.parseFile(this.selectedFile);
    } catch (error) {
      console.error('Failed to parse JUMega report input', error);
      parseResult = { records: [], errorMessage: 'The selected file could not be read.' };
    }

    if (!parseResult.records.length) {
      this.validationAlertMessage =
        parseResult.errorMessage ?? 'The selected file does not contain any data rows.';
      this.showValidationAlert = true;
      this.updateValidationFlags();
      this.cdr.markForCheck();
      return;
    }

    const payload: JumegaReportRequest = { cusipRecords: parseResult.records };
    this.isSubmitting = true;
    this.cdr.markForCheck();

    try {
      await firstValueFrom(this.service.generateReport(payload));
      this.isSuccessAlert = true;
      this.showValidationAlert = true;
      this.validationAlertMessage = 'Request processed. An email will be sent shortly.';
    } catch (error) {
      console.error('Failed to generate JUMega report', error);
      this.isSuccessAlert = false;
      this.showValidationAlert = true;
      this.validationAlertMessage =
        'There was an issue processing your request. Please try again later.';
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  private updateValidationFlags(): void {
    const fileValid = !!this.selectedFile && this.isValidFile();

    this.hasFileValidationError = !fileValid;
  }

  private async parseFile(file: File): Promise<JumegaParseResult> {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'xls' || extension === 'xlsx') {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return sheet
        ? this.parseRows(XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, { header: 1 }))
        : { records: [], errorMessage: 'The selected file does not contain a worksheet.' };
    }
    return this.parseRows(
      (await this.readFileText(file))
        .split(/\r?\n/)
        .filter(Boolean)
        .map((line) =>
          line.includes(',')
            ? line.split(',')
            : line.includes('\t')
              ? line.split('\t')
              : line.includes('|')
                ? line.split('|')
                : line.split(/\s+/),
        ),
    );
  }

  private readFileText(file: File): Promise<string> {
    if (typeof file.text === 'function') {
      return file.text();
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  private parseRows(rows: Array<Array<string | number | null>>): JumegaParseResult {
    if (!rows.length) {
      return { records: [], errorMessage: 'The selected file is empty.' };
    }

    const expectedHeaders = ['cusip', 'originalface', 'currentface'];
    const headers = rows[0].map((value) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ''),
    );
    if (
      headers.length !== expectedHeaders.length ||
      new Set(headers).size !== expectedHeaders.length ||
      expectedHeaders.some((header) => !headers.includes(header))
    ) {
      return {
        records: [],
        errorMessage:
          'The first row must contain cusip, originalFace, and currentFace. Header order, capitalization, and spaces are not significant.',
      };
    }

    const cusipIndex = headers.indexOf('cusip');
    const originalFaceIndex = headers.indexOf('originalface');
    const currentFaceIndex = headers.indexOf('currentface');

    const records: JumegaRecord[] = [];
    for (const [index, row] of rows.slice(1).entries()) {
      const rowNumber = index + 2;
      if (row.length !== expectedHeaders.length) {
        return {
          records: [],
          errorMessage: `Row ${rowNumber} must contain exactly three values.`,
        };
      }

      const cusip = String(row[cusipIndex] ?? '').trim();
      if (!this.isValidCusip(cusip)) {
        return {
          records: [],
          errorMessage: `Row ${rowNumber} contains an invalid CUSIP. CUSIPs must be nine uppercase letters or digits with a valid check digit.`,
        };
      }

      const originalFace = this.parseAmount(row[originalFaceIndex]);
      if (originalFace === null) {
        return {
          records: [],
          errorMessage: `Row ${rowNumber} has an invalid OriginalFace value. It must be a number greater than 0.`,
        };
      }

      const currentFace = this.parseAmount(row[currentFaceIndex]);
      if (currentFace === null) {
        return {
          records: [],
          errorMessage: `Row ${rowNumber} has an invalid CurrentFace value. It must be a number greater than 0.`,
        };
      }

      records.push({ cusip, originalFace, currentFace });
    }
    return { records: records.slice(0, 1000) };
  }

  private isValidCusip(cusip: string): boolean {
    if (!/^[A-Z0-9]{9}$/.test(cusip)) return false;

    const checksum = [...cusip.slice(0, 8)].reduce((sum, character, index) => {
      const value = Number.isNaN(Number(character))
        ? character.charCodeAt(0) - 'A'.charCodeAt(0) + 10
        : Number(character);
      const product = value * (index % 2 === 0 ? 1 : 2);
      return sum + Math.floor(product / 10) + (product % 10);
    }, 0);

    return (10 - (checksum % 10)) % 10 === Number(cusip[8]);
  }

  private parseAmount(value: string | number | null): number | null {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    if (typeof value === 'string' && !/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value.trim())) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private isValidFile(): boolean {
    const extension = this.selectedFile?.name.split('.').pop()?.toLowerCase() ?? '';
    return this.acceptedFileExtensions.has(extension);
  }
}
